import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { DynamicDialogConfig, DialogService } from 'primeng/dynamicdialog';
import { Subscription, interval, lastValueFrom, map } from 'rxjs';
import { PaymentRoundControllerService } from 'src/app/admin-api/api/paymentRoundController.service';
import { PaymentRoundSummary } from 'src/app/admin-api/model/paymentRoundSummary';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { PaymentRoundErrorReportComponent } from '../payment-round-error-report/payment-round-error-report.component';

@Component({
  selector: 'app-payment-round-report-modal',
  templateUrl: './payment-round-report-modal.component.html',
  styleUrls: ['./payment-round-report-modal.component.scss']
})
export class PaymentRoundReportModalComponent implements OnInit, OnDestroy {
  private paymentRoundService = inject(PaymentRoundControllerService);
  private dialogService = inject(DialogService);
  private config = inject(DynamicDialogConfig);
  summary: PaymentRoundSummary = this.config.data?.job ?? {};
  loadOnInit: boolean = this.config.data?.loadOnInit === null || this.config.data?.loadOnInit === undefined ? true : this.config.data?.loadOnInit;
  private refreshSubscription: Subscription | null = null;

  approvalRate: number;
  hasCriticalFailure: boolean;
  criticalFailureMessage: string = '';
  lastUpdateAt: Date | null = null;
  isRefreshing: boolean = false;

  get isRunning(): boolean {
    return (
      (this.summary.totalProcessed ?? 0) !==
      (this.summary.totalSubscribers ?? 0)
    );
  }

  get hasFailures(): boolean {
    return (
      (this.summary.failureActivation ?? 0) > 0 ||
      (this.summary.failureEdition ?? 0) > 0 ||
      (this.summary.failurePayment ?? 0) > 0 ||
      (this.summary.failureValidation ?? 0) > 0
    );
  }

  constructor() {
    this.computeDerivedFields();
    this.updateLastRefreshTimestamp();
  }

  async ngOnInit(): Promise<void> {
    // Sempre faz refresh imediato ao abrir o modal
    if (this.summary.paymentRoundId && this.loadOnInit) {
      LoaderService.showLoader();
      await this.refreshSummary(false);
      LoaderService.showLoader(false);
    }

    if (this.isRunning && this.summary.paymentRoundId) {
      this.refreshSubscription = interval(60_000).subscribe(() => {
        void this.refreshSummary(false);
      });
    }
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
  }

  async refreshNow(): Promise<void> {
    await this.refreshSummary(true);
  }

  openErrorReportModal(): void {
    if (!this.summary.paymentRoundId) {
      return;
    }

    this.dialogService.open(PaymentRoundErrorReportComponent, {
      data: {
        paymentRoundId: this.summary.paymentRoundId
      },
      header: `Detalhes de falhas - Round ${this.summary.paymentRoundId}`,
      width: '95vw',
      style: { 'max-width': '1400px' },
      contentStyle: { overflow: 'auto' }
    });
  }

  private computeDerivedFields(): void {
    const total =
      (this.summary.totalProcessed || 0) -
      (this.summary.totalProcessedBenefit || 0);
    const success =
      (this.summary.paymentSuccess || 0) -
      (this.summary.paymentSuccessBenefit || 0);
    this.approvalRate =
      total > 0 ? Math.round((success / total) * 10000) / 100 : 0;
    this.hasCriticalFailure =
      (this.summary.failureActivation ?? 0) > 0 ||
      (this.summary.failureEdition ?? 0) > 0;

    if (this.hasCriticalFailure) {
      const failures: string[] = [];
      if ((this.summary.failureActivation ?? 0) > 0)
        failures.push(
          `${this.summary.failureActivation} assinatura(s) com falha na Ativação`
        );
      if ((this.summary.failureEdition ?? 0) > 0)
        failures.push(
          `${this.summary.failureEdition} assinatura(s) com falha na Criação de Edição`
        );
      this.criticalFailureMessage =
        `⚠️ AÇÃO IMEDIATA NECESSÁRIA — ${failures.join(' e ')}. ` +
        `Contate o time de Operations imediatamente. ` +
        `Essas falhas impedem futuras cobranças.`;
      return;
    }

    this.criticalFailureMessage = '';
  }

  private async refreshSummary(showErrors: boolean): Promise<void> {
    if (!this.summary.paymentRoundId || this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;
    try {
      this.summary = await lastValueFrom(
        this.paymentRoundService
          .getPaymentRoundSummaryById(this.summary.paymentRoundId as number)
          .pipe(map((data) => data.result as PaymentRoundSummary))
      );
      this.computeDerivedFields();
      this.updateLastRefreshTimestamp();

      if (!this.isRunning) {
        this.refreshSubscription?.unsubscribe();
        this.refreshSubscription = null;
      }
    } catch (error) {
      if (showErrors) {
        AppDialogService.showErrorDialog(error);
      }
    } finally {
      this.isRefreshing = false;
    }
  }

  private updateLastRefreshTimestamp(): void {
    const reportTimestamp = this.summary.lastUpdated
      ? new Date(this.summary.lastUpdated)
      : null;
    this.lastUpdateAt = reportTimestamp ?? new Date();
  }
}
